import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Popper,
  PopperProps,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { HoverText } from '@campfire/hover-link';
import { useRegionCodes } from '../auto-location-field/use-region-codes/use-region-codes';
import {
  PHONE_COUNTRIES,
  PhoneCountry,
  findCountryByIso,
  parseNationalInput,
  splitE164,
  toE164,
} from './phone-countries';

interface PhoneNumberFieldProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
}

const DEFAULT_ISO = 'AU';
const COUNTRY_FIELD_WIDTH = 105;
const COUNTRY_POPUP_WIDTH = 320;

const CountryPopper = (props: PopperProps) => (
  <Popper {...props} placement='bottom-start' style={{ width: COUNTRY_POPUP_WIDTH }} />
);

const FlagIcon = ({ iso }: { iso: string }) => {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <Box
        width={20}
        height={15}
        display='flex'
        alignItems='center'
        justifyContent='center'
        style={{ borderRadius: 2, backgroundColor: '#ECECEC', flexShrink: 0 }}
      >
        <Typography style={{ fontSize: 8, fontWeight: 700, color: '#545454' }}>{iso}</Typography>
      </Box>
    );
  }

  return (
    <img
      src={`/flags/${iso.toLowerCase()}.svg`}
      alt=''
      aria-hidden
      loading='lazy'
      width={20}
      height={15}
      onError={() => setBroken(true)}
      style={{ borderRadius: 2, flexShrink: 0, objectFit: 'cover' }}
    />
  );
};

export const PhoneNumberField = (props: PhoneNumberFieldProps) => {
  const { value, onChange, id, label, required, disabled, error, helperText, fullWidth = true } = props;
  const { primaryRegion } = useRegionCodes();

  const tenantIso = useMemo(() => {
    const region = (primaryRegion || '')
      .split(',')[0]
      .trim()
      .toUpperCase();
    if (region === 'ANZ') return 'AU';
    if (region === 'UK') return 'GB';
    return findCountryByIso(region) ? region : DEFAULT_ISO;
  }, [primaryRegion]);

  const parsed = useMemo(() => splitE164(value || ''), [value]);
  const [manual, setManual] = useState(() => Boolean(value) && !value.startsWith('+'));
  const [pickedIso, setPickedIso] = useState<string>();

  // The value's dial code wins; a pick only breaks ties on shared codes or applies before anything is typed.
  const picked = findCountryByIso(pickedIso);
  const country = parsed.country
    ? picked && picked.dial === parsed.country.dial
      ? picked
      : parsed.country
    : picked || findCountryByIso(tenantIso) || PHONE_COUNTRIES[0];
  const pendingInternational = !parsed.country && (value || '').startsWith('+');
  const national = parsed.country ? parsed.national : pendingInternational ? value : (value || '').replace(/^0+/, '');

  const overrideLink = (text: string, onClick: () => void) => (
    <HoverText style={{ marginTop: 8, fontSize: 12 }} color='primary' variant='caption' onClick={onClick}>
      {text}
    </HoverText>
  );

  if (manual) {
    return (
      <Box>
        <TextField
          id={id}
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          variant='outlined'
          type='tel'
          fullWidth={fullWidth}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          placeholder='Enter the number exactly as it should be dialled'
        />
        {overrideLink('Back to guided entry', () => setManual(false))}
      </Box>
    );
  }

  return (
    <Box>
      <FormControl variant='outlined' fullWidth={fullWidth} error={error} disabled={disabled}>
        {label && (
          <InputLabel shrink htmlFor={id} required={required}>
            {label}
          </InputLabel>
        )}
        <OutlinedInput
          id={id}
          notched={Boolean(label)}
          label={label}
          value={national}
          onChange={(e) => onChange(parseNationalInput(e.target.value, country.dial))}
          type='tel'
          required={required}
          disabled={disabled}
          placeholder='412 345 678'
          startAdornment={
            <InputAdornment position='start' style={{ marginRight: 0 }}>
              <Autocomplete
                options={PHONE_COUNTRIES}
                value={country}
                disabled={disabled}
                disableClearable
                openOnFocus
                style={{ width: COUNTRY_FIELD_WIDTH }}
                PopperComponent={CountryPopper}
                getOptionLabel={(c: PhoneCountry) => `+${c.dial}`}
                getOptionSelected={(a: PhoneCountry, b: PhoneCountry) => a.iso === b.iso}
                filterOptions={(options: PhoneCountry[], state: { inputValue: string }) => {
                  const q = state.inputValue
                    .trim()
                    .toLowerCase()
                    .replace(/^\+/, '');
                  if (!q) return options;
                  return options.filter(
                    (c) => c.name.toLowerCase().includes(q) || c.dial.startsWith(q) || c.iso.toLowerCase() === q
                  );
                }}
                onChange={(_unusedEvent: unknown, next: PhoneCountry | null) => {
                  if (!next) return;
                  setPickedIso(next.iso);
                  onChange(toE164(next.dial, pendingInternational ? '' : national));
                }}
                renderOption={(c: PhoneCountry) => (
                  <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                    <FlagIcon iso={c.iso} />
                    <Typography variant='body2' noWrap style={{ flexGrow: 1 }}>
                      {c.name}
                    </Typography>
                    <Typography variant='body2' color='textSecondary' style={{ flexShrink: 0 }}>
                      +{c.dial}
                    </Typography>
                  </Box>
                )}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      style: { flexWrap: 'nowrap' },
                      startAdornment: (
                        <Box display='flex' alignItems='center' style={{ marginRight: 6, flexShrink: 0 }}>
                          <FlagIcon iso={country.iso} />
                        </Box>
                      ),
                    }}
                  />
                )}
              />
            </InputAdornment>
          }
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
      {overrideLink('Not a mobile, or need to enter it manually?', () => setManual(true))}
    </Box>
  );
};
